import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import { Card, CardHeader, ContentLoader, BackButton } from '@/components/common';
import { getChannelWorkspace } from '@/services/channels';

/**
 * Override rights (#339): "Preview as partner". A READ-ONLY look at a channel
 * partner that has no login of its own. Nothing here acts as anyone: there is
 * no token, no edit control, and the banner says so. Views are audited.
 */
export function ChannelWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['channel-workspace', id],
    queryFn: () => getChannelWorkspace(id as string),
    enabled: !!id,
    retry: false,
  });

  if (isLoading) return <ContentLoader />;
  if (isError || !data) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Channel not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          The channel does not exist or you cannot preview it.
        </p>
      </div>
    );
  }

  const { channel, policies, policy_summary: summary } = data;
  return (
    <div>
      <div className="mb-4">
        <BackButton to={`/broker/channels/${channel.id}`} />
      </div>
      <div
        role="status"
        className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
      >
        {data.banner}
      </div>
      <PageHeader title={channel.name} subtitle={channel.code} />
      <div className="space-y-6">
        <Card>
          <CardHeader title="Profile" />
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-900 dark:text-gray-100">
            <div><dt className="text-xs text-gray-500">Type</dt><dd>{channel.channel_type}</dd></div>
            <div><dt className="text-xs text-gray-500">Status</dt><dd>{channel.is_active ? 'Active' : 'Inactive'}</dd></div>
            <div><dt className="text-xs text-gray-500">Email</dt><dd>{channel.email || '-'}</dd></div>
            <div><dt className="text-xs text-gray-500">Phone</dt><dd>{channel.phone || '-'}</dd></div>
          </dl>
        </Card>
        <Card>
          <CardHeader
            title="Policies"
            subtitle={summary ? `${summary.count} policies, total premium ${summary.total_premium}` : undefined}
          />
          {data.policies_hidden ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Policy details need the policies read right.
            </p>
          ) : policies.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No policies sold through this channel yet.</p>
          ) : (
            <table className="min-w-full text-sm text-gray-900 dark:text-gray-100">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-2 pr-4">Policy</th>
                  <th className="py-2 pr-4">Holder</th>
                  <th className="py-2 pr-4">Insurer</th>
                  <th className="py-2 pr-4">Premium</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-2 pr-4">{p.policy_number}</td>
                    <td className="py-2 pr-4">{p.holder_name}</td>
                    <td className="py-2 pr-4">{p.insurer_name}</td>
                    <td className="py-2 pr-4">{p.total_premium}</td>
                    <td className="py-2">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
